import { TILE_H, TILE_W } from '../shopfloor/config/constants';
import { pack0 } from './assets/pack0';
import { pack1 } from './assets/pack1';
import { pack2 } from './assets/pack2';
import { pack3 } from './assets/pack3';
import { pack4 } from './assets/pack4';
import { pack5 } from './assets/pack5';
import { pack6 } from './assets/pack6';
import { pack7 } from './assets/pack7';
import { robotArm } from './assets/robotArm';

// Runtime asset catalogue for the Línea 750 view.
//
// The art and the factory geometry are deliberately separate:
//   topology/layout -> WHERE the entity is
//   this catalogue   -> HOW the art sits on that location
//
// Every groundAnchor below was measured against the runtime PNG's opaque pixels. The anchor is the
// point in the source image that is placed on the logical front/floor contact of the footprint.
// Never move tx/ty to compensate for a crop or transparent padding.
//
// The machine pack came from Vicente's original isometric simulator archive. It is REPRESENTATIVE
// art, not evidence of the exact make/model installed on Línea 750. That distinction is consistent
// with topology.layoutSource='derived': the view is a truthful process diagram until surveyed
// coordinates + exact machine identities are available.

const DEFAULT_PROFILE = {
  key: 'generic-machine',
  footprint: { w: 2, h: 2 },
  renderWidthPx: 118,
  groundAnchor: { x: 0.5, y: 1 },
  groundOffsetPx: { x: 0, y: 0 },
  labelLiftPx: 88,
  statusLiftPx: 72,
  alarmOffsetPx: { x: 54, y: -80 },
};

const PACKS = {
  pack0: {
    facings: pack0,
    anchors: { se: { x: 0.3200, y: 1 }, sw: { x: 0.6500, y: 1 } },
    renderWidthPx: 125,
  },
  pack1: {
    facings: pack1,
    anchors: { se: { x: 0.3214, y: 1 }, sw: { x: 0.6732, y: 1 } },
    renderWidthPx: 128,
  },
  pack2: {
    facings: pack2,
    anchors: { se: { x: 0.3241, y: 1 }, sw: { x: 0.7617, y: 1 } },
    renderWidthPx: 118,
  },
  pack3: {
    facings: pack3,
    anchors: { se: { x: 0.3488, y: 1 }, sw: { x: 0.6589, y: 1 } },
    renderWidthPx: 132,
  },
  pack4: {
    facings: pack4,
    anchors: { se: { x: 0.3785, y: 1 }, sw: { x: 0.6201, y: 1 } },
    renderWidthPx: 96,
  },
  pack5: {
    facings: pack5,
    anchors: { se: { x: 0.3173, y: 1 }, sw: { x: 0.6387, y: 1 } },
    renderWidthPx: 126,
  },
  pack6: {
    facings: pack6,
    anchors: { se: { x: 0.2738, y: 1 }, sw: { x: 0.4821, y: 1 } },
    renderWidthPx: 168,
  },
  pack7: {
    facings: pack7,
    anchors: { se: { x: 0.6111, y: 1 }, sw: { x: 0.3699, y: 1 } },
    renderWidthPx: 120,
  },
};

function fromPack(packKey, facing = 'se', over = {}) {
  const pack = PACKS[packKey];
  const f = pack?.facings?.[facing] ? facing : 'se';
  return {
    ...DEFAULT_PROFILE,
    key: `${packKey}-${f}`,
    spriteUrl: pack?.facings?.[f] || null,
    groundAnchor: pack?.anchors?.[f] || DEFAULT_PROFILE.groundAnchor,
    renderWidthPx: pack?.renderWidthPx || DEFAULT_PROFILE.renderWidthPx,
    ...over,
    facing: f,
    assetSource: 'vicente-machine-pack',
    assetProvenance: 'representative',
  };
}

// Temporary art assignment for the real Línea 750 topology.
//
// These are intentionally keyed by station, not only machineType. Four turning operations can use
// different representative CNC silhouettes/facings while the backend remains the authority on the
// fact that all four are LATHE resources. When exact machine identity arrives, this table is the
// only thing that changes.
const STATION_ASSETS = {
  OP10:  { pack: 'pack1', facing: 'se', footprint: { w: 2.0, h: 2.0 } },
  OP20:  { pack: 'pack1', facing: 'sw', footprint: { w: 2.0, h: 2.0 } },
  OP30:  { pack: 'pack2', facing: 'se', footprint: { w: 2.0, h: 2.0 } },
  OP40:  { pack: 'pack2', facing: 'sw', footprint: { w: 2.0, h: 2.0 } },
  OP60:  { pack: 'pack5', facing: 'se', footprint: { w: 2.0, h: 2.0 } },
  OP90:  { pack: 'pack0', facing: 'sw', footprint: { w: 1.8, h: 1.8 } },
  OP80:  { pack: 'pack3', facing: 'sw', footprint: { w: 1.7, h: 1.7 } },
  BANCO: { pack: 'pack4', facing: 'se', footprint: { w: 1.6, h: 1.3 }, renderWidthPx: 108 },
};

const TYPE_DEFAULTS = {
  LATHE:    { pack: 'pack1', facing: 'se' },
  MILL:     { pack: 'pack5', facing: 'se' },
  BALANCER: { pack: 'pack0', facing: 'se' },
  STATION:  { pack: 'pack3', facing: 'se' },
  GAUGE:    { pack: 'pack4', facing: 'se' },
};

// Pack 6 (large gantry/laser) is deliberately in the catalogue but not assigned to Línea 750.
// It remains available for future simulated equipment rather than being forced onto the wrong station.

export function lineAssetProfile(machineType, stationKey) {
  const exact = STATION_ASSETS[String(stationKey || '').toUpperCase()];
  const fallback = TYPE_DEFAULTS[String(machineType || '').toUpperCase()];
  const spec = exact || fallback;
  if (!spec) return { ...DEFAULT_PROFILE };

  return fromPack(spec.pack, spec.facing, {
    footprint: spec.footprint || DEFAULT_PROFILE.footprint,
    ...(spec.renderWidthPx ? { renderWidthPx: spec.renderWidthPx } : {}),
    labelLiftPx: spec.labelLiftPx ?? 104,
    statusLiftPx: spec.statusLiftPx ?? 88,
    alarmOffsetPx: spec.alarmOffsetPx || { x: 54, y: -94 },
    stationKey: stationKey || null,
  });
}

export function nodeAssetProfile(node) {
  if (node?.kind !== 'conveyor') return null;
  const facing = node.cellId === 'CELL-2' ? 'sw' : 'se';
  return fromPack('pack7', facing, {
    key: `conveyor-${facing}`,
    footprint: { w: 1.7, h: 0.8 },
    renderWidthPx: 108,
    labelLiftPx: 54,
    statusLiftPx: 42,
  });
}

export function robotAssetProfile(robotId) {
  return {
    ...DEFAULT_PROFILE,
    key: `robot-arm-${robotId || 'robot'}`,
    spriteUrl: robotArm.se,
    footprint: { w: 2.4, h: 2.4 },
    renderWidthPx: 152,
    // Measured from the cropped runtime image: bottom support contact sits left of visual centre.
    groundAnchor: { x: 0.4421, y: 1 },
    groundOffsetPx: { x: 0, y: 0 },
    labelLiftPx: 150,
    statusLiftPx: 132,
    alarmOffsetPx: { x: 60, y: -124 },
    facing: 'se',
    assetSource: 'vicente-uploaded-robot',
    assetProvenance: 'representative',
  };
}

export function footprintContactPx(profile) {
  const { w, h } = profile.footprint || DEFAULT_PROFILE.footprint;
  return {
    x: (w - h) * TILE_W / 2,
    y: (w + h) * TILE_H / 2,
  };
}

export function lineAssetUrls(topology) {
  const urls = new Set();
  for (const station of topology?.stations || []) {
    const url = lineAssetProfile(station.machineType, station.key).spriteUrl;
    if (url) urls.add(url);
  }
  for (const node of topology?.nodes || []) {
    const url = nodeAssetProfile(node)?.spriteUrl;
    if (url) urls.add(url);
  }
  for (const robot of topology?.robots || []) {
    const url = robotAssetProfile(robot.id)?.spriteUrl;
    if (url) urls.add(url);
  }
  return [...urls];
}
